import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Trash2, Check, Images, Loader2, Search } from 'lucide-react';
import { assetUrl } from '../../lib/siteAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LibraryTile = ({ img, selected, onPick, onDelete }) => (
  <div data-testid={`library-image-${img.id}`} className={`group relative rounded-xl overflow-hidden border-2 transition-colors ${selected ? 'border-[#C5A059]' : 'border-transparent hover:border-[#3B164C]/40'}`}>
    <button type="button" onClick={() => onPick(img)} data-testid={`library-pick-${img.id}`} className="block w-full aspect-square bg-[#F5EFE9]">
      <img src={assetUrl(img.url)} alt={img.name} loading="lazy" className="w-full h-full object-cover" />
    </button>
    {selected && <span className="absolute top-2 left-2 bg-[#C5A059] text-white rounded-full p-1"><Check size={12} /></span>}
    {img.in_use ? (
      <span data-testid={`library-in-use-${img.id}`} className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-[#3B164C] text-white px-2 py-0.5 rounded-full">In use</span>
    ) : (
      <button type="button" onClick={() => onDelete(img)} data-testid={`library-delete-${img.id}`} title="Delete image"
        className="absolute top-2 right-2 p-1.5 bg-white/90 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50">
        <Trash2 size={14} />
      </button>
    )}
    <p className="text-xs truncate px-2 py-1.5 text-[#1E1622]/70 bg-white">{img.name}</p>
  </div>
);

export const filterImages = (images, q, usage, source) => images.filter((i) => i.name.toLowerCase().includes(q.trim().toLowerCase())
  && (usage === 'all' || (usage === 'used') === i.in_use) && (source === 'all' || i.source === source));

const selCls = 'p-2 text-sm border border-[#E6DAC5] rounded-lg bg-white focus:border-[#3B164C] focus:outline-none';

export const LibraryFilters = ({ q, setQ, usage, setUsage, source, setSource }) => (
  <div className="flex flex-wrap gap-3 mb-4">
    <div className="relative flex-1 min-w-[180px]">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1E1622]/40" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" data-testid="library-search" className="w-full pl-9 p-2 text-sm border border-[#E6DAC5] rounded-lg focus:border-[#3B164C] focus:outline-none" />
    </div>
    <select value={usage} onChange={(e) => setUsage(e.target.value)} data-testid="library-filter-usage" className={selCls}>
      <option value="all">All images</option><option value="used">In use</option><option value="unused">Unused</option>
    </select>
    <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="library-filter-source" className={selCls}>
      <option value="all">All sources</option><option value="upload">Uploaded</option><option value="media">Linked</option>
    </select>
  </div>
);

export const ImageLibraryPicker = ({ token, current, onSelect }) => {
  const [q, setQ] = useState('');
  const [usage, setUsage] = useState('all');
  const [source, setSource] = useState('all');
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState(null);
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/admin/image-library`, headers).then((r) => setImages(r.data.images)).catch(() => { setImages([]); toast.error('Could not load the image library'); });

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (img) => {
    if (!window.confirm(`Delete "${img.name}" from the library?`)) return;
    try { await axios.delete(`${API}/admin/image-library/${img.id}`, headers); setImages((l) => l.filter((i) => i.id !== img.id)); toast.success('Image deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const shown = filterImages(images || [], q, usage, source);

  const pick = (img) => { onSelect(img.url); setOpen(false); toast.success('Image selected. Click Save to apply it'); };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="open-image-library"
        className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#3B164C]/30 text-[#3B164C] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
        <Images size={16} /> Choose from library
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="image-library-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#E6DAC5]">
              <div>
                <h3 className="font-display text-xl text-[#1E1622]">Image Library</h3>
                <p className="text-xs text-[#1E1622]/50">Images in use on the site can’t be deleted.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="close-image-library" className="p-2 hover:bg-[#F5EFE9] rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {images === null && <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#3B164C]" /></div>}
              {images?.length === 0 && <p data-testid="image-library-empty" className="text-center text-sm text-[#1E1622]/60 py-12">No uploaded images yet. Upload one and it will appear here.</p>}
              {images?.length > 0 && <LibraryFilters {...{ q, setQ, usage, setUsage, source, setSource }} />}
              {images?.length > 0 && shown.length === 0 && <p data-testid="library-no-matches" className="text-center text-sm text-[#1E1622]/60 py-10">No images match your search.</p>}
              {shown.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="library-grid">
                  {shown.map((img) => <LibraryTile key={img.id} img={img} selected={img.url === current} onPick={pick} onDelete={remove} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
